import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { nivelLabelColor } from "@/lib/constants";
import { Search } from "lucide-react";

export default function Avaliacoes() {
  const navigate = useNavigate();
  const location = useLocation();
  const [avs, setAvs] = useState([]);
  const [cursos, setCursos] = useState([]);
  const [tipologias, setTipologias] = useState([]);
  const [listas, setListas] = useState({ grau: [], instituicao: [], pais: [] });
  const [q, setQ] = useState("");
  const [fCurso, setFCurso] = useState("all");
  const [fStatus, setFStatus] = useState(() => {
    const s = new URLSearchParams(location.search).get("status");
    return s === "em_andamento" || s === "concluida" ? s : "all";
  });
  const [fTip, setFTip] = useState("all");
  const [fInst, setFInst] = useState("all");
  const [fGrau, setFGrau] = useState("all");
  const [fPais, setFPais] = useState("all");

  const load = () => {
    const params = new URLSearchParams();
    if (fCurso !== "all") params.set("curso_id", fCurso);
    if (fStatus !== "all") params.set("status", fStatus);
    if (fTip !== "all") params.set("tipologia_cec_id", fTip);
    if (fInst !== "all") params.set("aluno_instituicao", fInst);
    if (fGrau !== "all") params.set("aluno_grau", fGrau);
    if (fPais !== "all") params.set("aluno_pais", fPais);
    api.get(`/avaliacoes?${params.toString()}`).then((r) => setAvs(r.data));
  };
  useEffect(() => {
    api.get("/cursos").then((r) => setCursos(r.data));
    api.get("/tipologias-cec").then((r) => setTipologias(r.data));
    ["grau", "instituicao", "pais"].forEach((tipo) =>
      api.get(`/listas/${tipo}`).then((r) => setListas((s) => ({ ...s, [tipo]: r.data })))
    );
  }, []);
  useEffect(() => { load(); }, [fCurso, fStatus, fTip, fInst, fGrau, fPais]);

  const filtered = avs.filter((a) =>
    !q || a.aluno_nome?.toLowerCase().includes(q.toLowerCase()) ||
    a.oficina_nome?.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div>
      <button data-testid="voltar-btn" onClick={() => navigate("/")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-3">
        <span aria-hidden>←</span> Voltar
      </button>
      <PageHeader title="Avaliações" subtitle="Consulte, retome e exporte avaliações salvas" />

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="busca-avaliacoes" className="pl-9" placeholder="Buscar por aluno ou oficina" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <Select value={fCurso} onValueChange={setFCurso}>
          <SelectTrigger data-testid="filtro-curso" className="w-[180px]"><SelectValue placeholder="Curso" /></SelectTrigger>
          <SelectContent className="bg-popover border-border">
            <SelectItem value="all">Todos os cursos</SelectItem>
            {cursos.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fStatus} onValueChange={setFStatus}>
          <SelectTrigger data-testid="filtro-status" className="w-[170px]"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent className="bg-popover border-border">
            <SelectItem value="all">Todos os status</SelectItem>
            <SelectItem value="em_andamento">Em andamento</SelectItem>
            <SelectItem value="concluida">Concluída</SelectItem>
          </SelectContent>
        </Select>
        <Select value={fTip} onValueChange={setFTip}>
          <SelectTrigger data-testid="filtro-tipologia" className="w-[190px]"><SelectValue placeholder="Tipologia CEC" /></SelectTrigger>
          <SelectContent className="bg-popover border-border">
            <SelectItem value="all">Todas as tipologias</SelectItem>
            {tipologias.map((t) => <SelectItem key={t.id} value={t.id}>{t.nome}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fInst} onValueChange={setFInst}>
          <SelectTrigger data-testid="filtro-instituicao" className="w-[170px]"><SelectValue placeholder="Instituição" /></SelectTrigger>
          <SelectContent className="bg-popover border-border">
            <SelectItem value="all">Todas as instituições</SelectItem>
            {listas.instituicao.map((it) => <SelectItem key={it.id} value={it.nome}>{it.nome}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fGrau} onValueChange={setFGrau}>
          <SelectTrigger data-testid="filtro-grau" className="w-[160px]"><SelectValue placeholder="Grau" /></SelectTrigger>
          <SelectContent className="bg-popover border-border">
            <SelectItem value="all">Todos os graus</SelectItem>
            {listas.grau.map((g) => <SelectItem key={g.id} value={g.nome}>{g.nome}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fPais} onValueChange={setFPais}>
          <SelectTrigger data-testid="filtro-pais" className="w-[150px]"><SelectValue placeholder="País" /></SelectTrigger>
          <SelectContent className="bg-popover border-border">
            <SelectItem value="all">Todos os países</SelectItem>
            {listas.pais.map((p) => <SelectItem key={p.id} value={p.nome}>{p.nome}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="bg-card border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead>Aluno</TableHead>
              <TableHead className="hidden md:table-cell">Oficina</TableHead>
              <TableHead className="hidden sm:table-cell">Data</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Resultado</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((a) => (
              <TableRow key={a.id} data-testid={`row-av-${a.id}`} className="border-border cursor-pointer" onClick={() => navigate(`/avaliacoes/${a.id}`)}>
                <TableCell className="font-medium">{a.aluno_nome}{a.aluno_nome_guerra ? <span className="text-xs text-muted-foreground font-normal"> "{a.aluno_nome_guerra}"</span> : ""}</TableCell>
                <TableCell className="hidden md:table-cell text-muted-foreground">{a.oficina_nome}</TableCell>
                <TableCell className="hidden sm:table-cell text-muted-foreground font-mono text-xs">
                  {(a.finalized_at || a.created_at || "").slice(0, 10)}
                </TableCell>
                <TableCell>
                  {a.status === "concluida"
                    ? <Badge className="bg-teal-500/20 text-teal-300 border-teal-500/40">Concluída</Badge>
                    : <Badge className="bg-orange-500/20 text-orange-300 border-orange-500/40">Em andamento</Badge>}
                </TableCell>
                <TableCell className="text-right">
                  {a.resultado?.nivel_geral?.label
                    ? <Badge className={nivelLabelColor(a.resultado.nivel_geral.label)}>{a.resultado.nivel_geral.label}</Badge>
                    : <span className="text-muted-foreground text-xs">—</span>}
                </TableCell>
              </TableRow>
            ))}
            {!filtered.length && (
              <TableRow><TableCell colSpan={5} className="text-center py-10 text-muted-foreground">Nenhuma avaliação encontrada.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
